import type { CountryCodes, Iso3166Alpha2Lower } from "../types/country-codes";
import type { LocaleDomain } from "../types/locale-domain";

type CountryDefinition = CountryCodes & {
  domain: LocaleDomain;
};

const COUNTRY_DEFINITIONS: Record<Iso3166Alpha2Lower, CountryDefinition> = {
  ca: {
    clientNoticeOfPrivacyPracticesPath:
      "/legal/client-notice-of-privacy-practices",
    defaultMapZoom: 4,
    displayName: "Canada",
    domain: "homeinstead.ca",
    geographicCenter: { lat: 56.1304, lng: -106.3468 },
    iso3166Alpha2: "CA",
    iso3166Alpha2Lower: "ca",
    iso3166Alpha3: "CAN",
    iso3166Alpha3Lower: "can",
    iso4217CurrencyCode: "CAD",
    locationSearchPlaceholder: "Postal Code, City or Province",
    postalCodeFormatErrorMessage:
      "Postal code must be a valid Canadian postal code",
    postalCodeRequiredErrorMessage: "Please enter a postal code.",
    postalLocationRequiredErrorMessage: "Please select a location.",
    privacyPolicyPath: "/legal/privacy-policy",
    whatsNextToolkitUrl:
      "https://cdn.sanity.io/files/pxhqtzxs/production/b1369c0a70844732923919b173812a9f3df9dc16.pdf",
  },
  ie: {
    clientNoticeOfPrivacyPracticesPath:
      "/legal/client-notice-of-privacy-practices",
    defaultMapZoom: 6,
    displayName: "Ireland",
    domain: "homeinstead.ie",
    geographicCenter: { lat: 53.4214, lng: -8.2439 },
    iso3166Alpha2: "IE",
    iso3166Alpha2Lower: "ie",
    iso3166Alpha3: "IRL",
    iso3166Alpha3Lower: "irl",
    iso4217CurrencyCode: "EUR",
    locationSearchPlaceholder: "Eircode, City or County",
    postalCodeFormatErrorMessage: "Must be a valid Eircode",
    postalCodeRequiredErrorMessage: "Please enter an Eircode.",
    postalLocationRequiredErrorMessage: "Please select a location.",
    privacyPolicyPath: "/legal/privacy-policy",
    whatsNextToolkitUrl: null,
  },
  us: {
    clientNoticeOfPrivacyPracticesPath:
      "/legal/client-notice-of-privacy-practices",
    defaultMapZoom: 4,
    displayName: "United States",
    domain: "homeinstead.com",
    geographicCenter: { lat: 39.8283, lng: -98.5795 },
    iso3166Alpha2: "US",
    iso3166Alpha2Lower: "us",
    iso3166Alpha3: "USA",
    iso3166Alpha3Lower: "usa",
    iso4217CurrencyCode: "USD",
    locationSearchPlaceholder: "Postal Code, City or State",
    postalCodeFormatErrorMessage: "ZIP code must be a valid US ZIP code",
    postalCodeRequiredErrorMessage: "Please enter a ZIP code.",
    postalLocationRequiredErrorMessage: "Please select a location.",
    privacyPolicyPath: "/legal/privacy-policy",
    whatsNextToolkitUrl:
      "https://cdn.sanity.io/files/pxhqtzxs/production/8b536bf76b5c293b380e7ce1611e4977887a55de.pdf",
  },
};

function getCountryDefinition(
  iso3166Alpha2Lower: Iso3166Alpha2Lower,
): CountryDefinition {
  return COUNTRY_DEFINITIONS[iso3166Alpha2Lower];
}

function isIso3166Alpha2Lower(value: string): value is Iso3166Alpha2Lower {
  return value in COUNTRY_DEFINITIONS;
}

export { getCountryDefinition, isIso3166Alpha2Lower };
